import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowUpRight } from '@phosphor-icons/react';
import { API } from '../lib/api';
import { useSEO } from '../lib/seo';
import { useAuthUser } from '../lib/useAuth';
import { DashboardShell, EmptyState, StatCard } from '../components/dashboard/Bits';

const Dashboard = () => {
  useSEO({ title: 'Dashboard | Vox Meridian', description: '', path: '/dashboard', robots: 'noindex, nofollow' });
  const auth = useAuthUser();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (auth.status !== 'signed-in') return;
    let active = true;
    setLoading(true);
    axios
      .get(`${API}/dashboard/summary`, { withCredentials: true })
      .then(({ data }) => active && setSummary(data))
      .catch(() => active && setSummary(null))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [auth.status]);

  if (auth.status === 'checking') {
    return <div className="flex min-h-[70vh] items-center justify-center bg-ink" data-testid="dashboard-loading"><p className="mono-label text-steel">Checking your session…</p></div>;
  }

  if (auth.status === 'signed-out') {
    return (
      <DashboardShell eyebrow="Dashboard" title={<>Sign in first.</>} sub="Use your Google account to open your buyer or provider dashboard.">
        <EmptyState
          testId="dashboard-signed-out"
          title="Google sign-in required."
          text="Public RFQ and provider forms stay open. The dashboard is only for tracking your own submissions and quotes."
          actionLabel="Go to sign in"
          actionTo="/account"
        />
      </DashboardShell>
    );
  }

  const stats = summary?.stats || {};
  return (
    <DashboardShell
      eyebrow="Dashboard"
      title={<>Your workspace.</>}
      sub={`Signed in as ${auth.user.email}. Buyer and provider records are linked automatically by email.`}
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="dashboard-stats">
        <StatCard label="My RFQs" value={stats.buyer_rfqs ?? '—'} testId="dashboard-stat-buyer-rfqs" />
        <StatCard label="Quotes waiting" value={stats.buyer_quotes_waiting ?? '—'} testId="dashboard-stat-quotes-waiting" />
        <StatCard label="Matched RFQs" value={stats.provider_matched_rfqs ?? '—'} testId="dashboard-stat-matched-rfqs" />
        <StatCard label="Jobs won" value={stats.provider_jobs_won ?? '—'} testId="dashboard-stat-jobs-won" />
      </div>

      <div className="mt-8 grid gap-4 lg:grid-cols-2">
        <div className="border border-line bg-surface p-8" data-testid="dashboard-buyer-card">
          <p className="mono-label text-volt">Buyer</p>
          <h2 className="mt-3 font-display text-2xl font-black text-parchment">Track RFQs and quotes.</h2>
          <p className="mt-3 text-sm leading-relaxed text-steel">See every RFQ submitted with this email, compare quotations, accept or decline, and mark work complete.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/dashboard/buyer" data-testid="dashboard-open-buyer" className="inline-flex items-center gap-2 bg-volt px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink">
              Open buyer dashboard <ArrowUpRight size={14} weight="bold" />
            </Link>
            <Link to="/dashboard/services" data-testid="dashboard-open-services" className="inline-flex items-center gap-2 border border-line px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-parchment transition-colors duration-200 hover:border-volt hover:text-volt">
              Vox Service Manager <ArrowUpRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
        <div className="border border-line bg-surface p-8" data-testid="dashboard-provider-card">
          <p className="mono-label text-volt">Provider</p>
          <h2 className="mt-3 font-display text-2xl font-black text-parchment">See matched RFQs.</h2>
          <p className="mt-3 text-sm leading-relaxed text-steel">
            {summary?.provider_status === 'founder_review'
              ? 'Your provider application is under founder review. You will see matched RFQs after verification.'
              : 'View RFQs matched to your services, markets and delivery modes, request access, and submit quotes.'}
          </p>
          <Link to="/dashboard/provider" data-testid="dashboard-open-provider" className="mt-6 inline-flex items-center gap-2 border border-line px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-parchment transition-colors duration-200 hover:border-volt hover:text-volt">
            Open provider dashboard <ArrowUpRight size={14} weight="bold" />
          </Link>
        </div>
      </div>

      {!loading && summary && !summary.has_buyer && !summary.has_provider && (
        <div className="mt-8">
          <EmptyState
            testId="dashboard-empty"
            title="No records linked yet."
            text="This Google account has no RFQ or provider application yet. Submit one publicly and it will appear here automatically."
            actionLabel="Post an RFQ"
            actionTo="/buyers"
          />
        </div>
      )}
    </DashboardShell>
  );
};

export default Dashboard;
